import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  FileQuestion,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Upload,
  ArrowLeft,
  CheckSquare,
  ToggleLeft,
  AlignLeft,
  FileText,
  Link2,
  ListOrdered,
  Tag,

  FolderOpen,
  FolderInput,
  Loader2,
  AlertCircle,
  UploadCloud,
  FileSpreadsheet,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  single_choice: { label: 'QCM unique', icon: CheckSquare, color: 'bg-blue-500' },
  multiple_choice: { label: 'QCM multiple', icon: CheckSquare, color: 'bg-indigo-500' },
  true_false: { label: 'Vrai/Faux', icon: ToggleLeft, color: 'bg-green-500' },
  short_answer: { label: 'Réponse courte', icon: AlignLeft, color: 'bg-yellow-500' },
  long_answer: { label: 'Réponse longue', icon: FileText, color: 'bg-orange-500' },
  matching: { label: 'Appariement', icon: Link2, color: 'bg-purple-500' },
  ordering: { label: 'Classement', icon: ListOrdered, color: 'bg-pink-500' },
};

const difficultyConfig = {
  easy: { label: 'Facile', color: 'bg-green-100 text-green-800' },
  medium: { label: 'Moyen', color: 'bg-yellow-100 text-yellow-800' },
  hard: { label: 'Difficile', color: 'bg-red-100 text-red-800' },
};

export default function QuestionBankPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importFormat, setImportFormat] = useState('csv');
  const [importCategory, setImportCategory] = useState('');
  const [importing, setImporting] = useState(false);
  
  // Selection state
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'all';

  // Active category tab
  const [activeTab, setActiveTab] = useState(initialCat);
  
  // Category management
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      loadQuestions();
      loadCategories();
    }
  }, [workspaceId, typeFilter, difficultyFilter, categoryFilter]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/exams/questions?workspace_id=${workspaceId}`;
      
      if (typeFilter && typeFilter !== 'all') {
        url += `&q_type=${typeFilter}`;
      }
      if (difficultyFilter && difficultyFilter !== 'all') {
        url += `&difficulty=${difficultyFilter}`;
      }
      if (categoryFilter && categoryFilter !== 'all') {
        url += `&category=${categoryFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      setQuestions(data);
    } catch (error) {
      console.error('Failed to load questions:', error);
      toast.error('Erreur lors du chargement des questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadQuestions();
  };

  const handleDuplicate = async (questionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/exams/questions/${questionId}/duplicate?workspace_id=${workspaceId}`,
        { method: 'POST' }
      );
      if (response.ok) {
        toast.success('Question dupliquée');
        loadQuestions();
      }
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleDelete = async (questionId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette question ?')) return;
    
    try {
      await fetch(`${API_URL}/api/exams/questions/${questionId}?workspace_id=${workspaceId}`, {
        method: 'DELETE',
      });
      toast.success('Question supprimée');
      loadQuestions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Selection handlers
  const toggleSelectQuestion = (questionId) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedQuestions.size === questions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(questions.map(q => q.id)));
    }
  };

  const selectAllInCategory = (category) => {
    const categoryQuestions = questions.filter(q => {
      let catName = 'Sans catégorie';
      if (q.category) {
        catName = typeof q.category === 'object' ? q.category.name : q.category;
      }
      return catName === category;
    });
    const newSelected = new Set(selectedQuestions);
    const allSelected = categoryQuestions.every(q => selectedQuestions.has(q.id));
    
    if (allSelected) {
      categoryQuestions.forEach(q => newSelected.delete(q.id));
    } else {
      categoryQuestions.forEach(q => newSelected.add(q.id));
    }
    setSelectedQuestions(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.size === 0) return;
    
    setDeleting(true);
    try {
      const deletePromises = Array.from(selectedQuestions).map(questionId =>
        fetch(`${API_URL}/api/exams/questions/${questionId}?workspace_id=${workspaceId}`, {
          method: 'DELETE',
        })
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedQuestions.size} question(s) supprimée(s)`);
      setSelectedQuestions(new Set());
      setDeleteDialogOpen(false);
      loadQuestions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk assign category
  const handleBulkAssignCategory = async (categoryName) => {
    if (selectedQuestions.size === 0) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/exams/categories/assign-bulk?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question_ids: Array.from(selectedQuestions),
            category: categoryName || null
          })
        }
      );
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.modified_count} question(s) déplacée(s) vers "${categoryName || 'Sans catégorie'}"`);
        setSelectedQuestions(new Set());
        loadQuestions();
        loadCategories();
      } else {
        toast.error('Erreur lors du déplacement');
      }
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Group questions by admin-created categories
  const groupedQuestions = (() => {
    const groups = {};
    // Initialize groups from admin categories
    categories.forEach(cat => {
      const name = cat.name || cat;
      groups[name] = [];
    });
    // Distribute questions
    questions.forEach(question => {
      let categoryName = null;
      if (question.category) {
        categoryName = typeof question.category === 'object' ? question.category.name : question.category;
      }
      if (categoryName && groups[categoryName] !== undefined) {
        groups[categoryName].push(question);
      } else {
        if (!groups['Sans catégorie']) groups['Sans catégorie'] = [];
        groups['Sans catégorie'].push(question);
      }
    });
    return groups;
  })();

  // Load categories
  const loadCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/exams/categories?workspace_id=${workspaceId}`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Add new category
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setSavingCategory(true);
    try {
      const res = await fetch(`${API_URL}/api/exams/categories?workspace_id=${workspaceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      
      if (res.ok) {
        toast.success('Catégorie créée');
        setNewCategoryName('');
        loadCategories();
      } else {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingCategory(false);
    }
  };

  // Delete category
  const deleteCategory = async (categoryId) => {
    if (!confirm('Supprimer cette catégorie ? Les questions ne seront pas supprimées.')) return;
    
    try {
      await fetch(`${API_URL}/api/exams/categories/${categoryId}?workspace_id=${workspaceId}`, {
        method: 'DELETE'
      });
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Load categories when dialog opens
  useEffect(() => {
    if ((categoryDialogOpen || importDialogOpen) && workspaceId) {
      loadCategories();
    }
  }, [categoryDialogOpen, importDialogOpen, workspaceId]);

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('workspace_id', workspaceId);
      formData.append('format', importFormat);
      formData.append('user_id', user?.id || '');
      if (importCategory) {
        formData.append('category', importCategory);
      }
      
      const response = await fetch(`${API_URL}/api/exams/questions/import`, {
        method: 'POST',
        body: formData,
      });
      
      const result = await response.json();
      
      if (result.imported_count > 0) {
        toast.success(`${result.imported_count} questions importées`);
        loadQuestions();
        setImportDialogOpen(false);
        setImportFile(null);
      } else {
        toast.error('Aucune question importée');
      }
      
      if (result.failed_count > 0) {
        toast.warning(`${result.failed_count} questions en erreur`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <WorkspaceLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/exams`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileQuestion className="h-7 w-7 text-primary" />
                Banque de questions
              </h1>
              <p className="text-muted-foreground mt-1">
                {questions.length} question(s) au total
                {selectedQuestions.size > 0 && (
                  <span className="ml-2 text-primary font-medium">
                    • {selectedQuestions.size} sélectionnée(s)
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Selection actions */}
            {selectedQuestions.size > 0 && canManage && (
              <>
              <Button 
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="btn-delete-selected"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ({selectedQuestions.size})
              </Button>
              
              {/* Bulk move to category */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="btn-move-to-category">
                    <FolderInput className="h-4 w-4 mr-2" />
                    Déplacer vers ({selectedQuestions.size})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {categories.map((cat) => (
                    <DropdownMenuItem 
                      key={cat.id || cat.name || cat}
                      onClick={() => handleBulkAssignCategory(cat.name || cat)}
                    >
                      <FolderOpen className="h-4 w-4 mr-2 text-amber-500" />
                      {cat.name || cat}
                    </DropdownMenuItem>
                  ))}
                  {categories.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={() => handleBulkAssignCategory(null)}>
                    <FolderOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                    Sans catégorie
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            )}
            

            
            {canManage && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => setCategoryDialogOpen(true)}
                  data-testid="btn-manage-categories"
                >
                  <Tag className="h-4 w-4 mr-2" />
                  Catégories
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setImportDialogOpen(true)}
                  data-testid="btn-import-questions"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Importer
                </Button>
                <Button 
                  onClick={() => navigate(`/workspace/${workspaceId}/exams/questions/new`)}
                  data-testid="btn-create-question"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle question
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une question..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-questions"
                />
              </div>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {Object.entries(typeConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
                <SelectTrigger className="w-[150px]" data-testid="filter-difficulty">
                  <SelectValue placeholder="Difficulté" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="easy">Facile</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="hard">Difficile</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-category">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id || cat} value={cat.name || cat}>{cat.name || cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button type="submit" variant="secondary">
                Rechercher
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'all'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              Toutes
              <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">{questions.length}</span>
            </button>
            {categories.map((cat) => {
              const catName = cat.name || cat;
              const count = questions.filter(q => {
                const qCat = q.category ? (typeof q.category === 'object' ? q.category.name : q.category) : null;
                return qCat === catName;
              }).length;
              return (
                <button
                  key={cat.id || catName}
                  onClick={() => setActiveTab(catName)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === catName
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <FolderOpen className="h-4 w-4" />
                  {catName}
                  <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">{count}</span>
                </button>
              );
            })}
            <button
              onClick={() => setActiveTab('__uncategorized__')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === '__uncategorized__'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              Sans catégorie
              <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">
                {questions.filter(q => !q.category).length}
              </span>
            </button>
          </div>
        )}

        {/* Questions Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (() => {
              const filteredQuestions = activeTab === 'all' || categories.length === 0
                ? questions
                : activeTab === '__uncategorized__'
                  ? questions.filter(q => !q.category)
                  : questions.filter(q => {
                      const qCat = q.category ? (typeof q.category === 'object' ? q.category.name : q.category) : null;
                      return qCat === activeTab;
                    });
              
              return filteredQuestions.length === 0 ? (
                <div className="text-center py-12">
                  <FileQuestion className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">Aucune question</h3>
                  <p className="text-muted-foreground mb-4">
                    {activeTab !== 'all' ? 'Aucune question dans cette catégorie' : 'Créez ou importez vos premières questions'}
                  </p>
                  {canManage && activeTab === 'all' && (
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                        <Upload className="h-4 w-4 mr-2" />
                        Importer
                      </Button>
                      <Button onClick={() => navigate(`/workspace/${workspaceId}/exams/questions/new`)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Créer
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={filteredQuestions.every(q => selectedQuestions.has(q.id)) && filteredQuestions.length > 0}
                          onCheckedChange={() => {
                            const allSelected = filteredQuestions.every(q => selectedQuestions.has(q.id));
                            const newSelected = new Set(selectedQuestions);
                            if (allSelected) {
                              filteredQuestions.forEach(q => newSelected.delete(q.id));
                            } else {
                              filteredQuestions.forEach(q => newSelected.add(q.id));
                            }
                            setSelectedQuestions(newSelected);
                          }}
                        />
                      </TableHead>
                      <TableHead className="w-[45%]">Question</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="hidden md:table-cell">Difficulté</TableHead>
                      <TableHead className="hidden md:table-cell">Points</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuestions.map((question) => {
                      const type = typeConfig[question.type] || typeConfig.single_choice;
                      const diff = difficultyConfig[question.difficulty] || difficultyConfig.medium;
                      const TypeIcon = type.icon;
                      
                      return (
                        <TableRow 
                          key={question.id} 
                          data-testid={`question-row-${question.id}`}
                          className={selectedQuestions.has(question.id) ? 'bg-primary/5' : ''}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedQuestions.has(question.id)}
                              onCheckedChange={() => toggleSelectQuestion(question.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium line-clamp-2">
                                {question.question_text?.replace(/<[^>]*>/g, '')}
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {activeTab === 'all' && question.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {typeof question.category === 'object' ? question.category.name : question.category}
                                  </Badge>
                                )}
                                {question.tags?.slice(0, 2).map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    <Tag className="h-3 w-3 mr-1" />
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${type.color} text-white`}>
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {type.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="outline" className={diff.color}>
                              {diff.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {question.points} pt{question.points > 1 ? 's' : ''}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/workspace/${workspaceId}/exams/questions/${question.id}`)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(question.id)}>
                                  <Copy className="h-4 w-4 mr-2" />
                                  Dupliquer
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => handleDelete(question.id)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
            })()}
          </CardContent>
        </Card>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <UploadCloud className="h-5 w-5 text-indigo-500" />
                Importer des questions
              </DialogTitle>
              <DialogDescription>
                Importez des questions depuis un fichier CSV ou HTML
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-5 py-2">
              {/* Format selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                  Format du fichier
                </label>
                <Select value={importFormat} onValueChange={setImportFormat}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">
                      <span className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                        CSV
                      </span>
                    </SelectItem>
                    <SelectItem value="html">
                      <span className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-orange-500" />
                        HTML
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* File upload area */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  Fichier
                </label>
                <label
                  className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition-all hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/20"
                  style={{
                    borderColor: importFile ? '#6366f1' : 'hsl(var(--border))',
                    background: importFile ? 'rgba(99,102,241,0.04)' : 'transparent'
                  }}
                >
                  <div className="flex flex-col items-center justify-center py-3">
                    {importFile ? (
                      <>
                        <CheckCircle2 className="h-8 w-8 text-emerald-500 mb-2" />
                        <p className="text-sm font-medium text-foreground">{importFile.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(importFile.size / 1024).toFixed(1)} Ko · Cliquez pour changer
                        </p>
                      </>
                    ) : (
                      <>
                        <UploadCloud className="h-8 w-8 text-muted-foreground/60 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          <span className="font-semibold text-indigo-500">Cliquez pour choisir</span> un fichier
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {importFormat === 'csv' ? '.csv, .txt' : '.html, .htm'}
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept={importFormat === 'csv' ? '.csv,.txt' : '.html,.htm'}
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="import-file-input"
                  />
                </label>
              </div>
              
              {/* Category selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  Catégorie (optionnel)
                </label>
                <Select value={importCategory || 'none'} onValueChange={(v) => setImportCategory(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Aucune catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune catégorie</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id || cat.name} value={cat.name || cat}>
                        {cat.name || cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Toutes les questions importées seront assignées à cette catégorie
                </p>
              </div>
              
              {/* CSV format info */}
              {importFormat === 'csv' && (
                <div className="rounded-xl border bg-muted/40 p-4">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-indigo-500" />
                    Format CSV attendu
                  </p>
                  <div className="bg-background rounded-lg p-3 border">
                    <code className="text-xs text-muted-foreground break-all leading-relaxed" style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: '0.7rem' }}>
                      id, type, question, choices, correct_answers, points, category, difficulty, explanation
                    </code>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                    <div><span className="font-medium text-foreground">type:</span> qcm, qcu, vf, text</div>
                    <div><span className="font-medium text-foreground">choices:</span> opt1|opt2|opt3</div>
                    <div><span className="font-medium text-foreground">difficulty:</span> Facile, Moyen, Difficile</div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setImportDialogOpen(false)} className="px-5">
                Annuler
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing || !importFile}
                className="px-6 gap-2"
                style={{ background: importing ? undefined : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Importer
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                Confirmer la suppression
              </DialogTitle>
              <DialogDescription>
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Vous êtes sur le point de supprimer <strong className="text-foreground">{selectedQuestions.size} question(s)</strong>.
              </p>
              <p className="text-sm text-red-500 mt-2">
                Ces questions seront définitivement supprimées de la banque de questions.
              </p>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer {selectedQuestions.size} question(s)
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Category Management Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                Gérer les catégories
              </DialogTitle>
              <DialogDescription>
                Créez et gérez les catégories pour organiser vos questions.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {/* Add new category */}
              <div className="flex gap-2">
                <Input
                  placeholder="Nouvelle catégorie..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                />
                <Button onClick={addCategory} disabled={savingCategory || !newCategoryName.trim()}>
                  {savingCategory ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                </Button>
              </div>
              
              {/* Category list */}
              <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                {categories.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground">
                    <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune catégorie</p>
                  </div>
                ) : (
                  categories.map((cat) => (
                    <div key={cat.id || cat.name} className="flex items-center justify-between p-3 hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-amber-500" />
                        <span className="font-medium">{cat.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {cat.question_count || 0} question(s)
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deleteCategory(cat.name)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
              
              <p className="text-xs text-muted-foreground">
                💡 Les catégories sont gérées manuellement. Créez vos catégories ici, puis assignez-les aux questions.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </WorkspaceLayout>
  );
}
